/**
 * Waagerechtes Blättern mit eigener Physik.
 *
 * Herausgezogen aus dem Projekt-Regal, damit Regal UND Veröffentlichungen
 * sich gleich anfühlen, ohne dass die Mechanik zweimal im Code steht.
 *
 * Warum nicht das native Scrollen des Browsers?
 *   Weil uns dann die Physik nicht gehört. Hier folgt die Spur dem Finger
 *   eins zu eins, gibt an den Enden nach wie ein Gummiband, und beim
 *   Loslassen entscheidet der Schwung: die Position wird um die Geschwindigkeit
 *   weiter projiziert (MOMENTUM_MS), und das naechstgelegene Element zu dieser
 *   Stelle gewinnt. Ein langsamer, kurzer Zug trifft damit praezise ein
 *   Element, ein schneller, kurzer Wisch traegt weiter und kann mehrere
 *   ueberspringen – wie ein Stapel Buecher, durch den man blaettert, nicht
 *   ein harter Seite-fuer-Seite-Wechsel.
 *
 * Aufbau, den der Aufrufer liefern muss:
 *   rail   schneidet ab (overflow: hidden)
 *     track  wird verschoben (transform)
 *       ...  die Elemente
 */
import { drawsWith } from '../features/drawing/tools.ts';

/** Ruhige, leicht traege Ausschwing-Kurve: kommt weich zur Ruhe, ohne hart
 *  einzurasten – wie ein Buch, das man auslaufen laesst statt es zu stoppen. */
const SETTLE = 'cubic-bezier(0.16, 1, 0.3, 1)';
/** Kuerzeste und laengste Einschwingzeit; siehe settleDuration(). */
const SETTLE_MIN = 360;
const SETTLE_MAX = 900;
/**
 * Wie weit der Schwung beim Loslassen noch nachwirkt (in Millisekunden
 * projizierte Zeit). Groesser = ein kurzer, schneller Wisch traegt weiter und
 * ueberspringt mehr Elemente – genau das "Momentum" aus mehreren Buechern.
 */
const MOMENTUM_MS = 380;
/** Wie stark es ueber den Enden nachgibt. 1 = gar nicht. */
const RUBBER = 0.32;
/** So lange nach einem Wisch wird der nachfolgende Klick verschluckt. */
const CLICK_GUARD = 120;

export interface SwipeRailOptions {
  /** Schneidet ab. */
  rail: HTMLElement;
  /** Wird verschoben; seine Kinder sind die Elemente. */
  track: HTMLElement;
  /** Wird gerufen, wenn ein anderes Element in der Mitte steht. */
  onIndexChange?: (index: number) => void;
  /**
   * Zeiger, die nicht blaettern sollen – z. B. Knoepfe auf den Elementen.
   * Bekommt das Ziel des Ereignisses.
   */
  ignore?: (target: HTMLElement) => boolean;
  /**
   * Wo das aktive Element steht.
   * "center" – mittig, wie im Regal: dort ist ein Buch ausgewaehlt.
   * "start"  – linksbuendig, wie eine Reihe zum Stoebern: man sieht sofort,
   *            was man hat, statt nur das erste Element in der Mitte.
   * Standard: "center".
   */
  align?: 'center' | 'start';
}

export interface SwipeRail {
  /** Das Element, das gerade in der Mitte steht. */
  index(): number;
  /** Dorthin blättern. */
  goTo(index: number, animate?: boolean): void;
  /** Ohne Animation neu ausrichten, z. B. nach dem Drehen des iPads. */
  recenter(): void;
  /** true, solange der Klick nach einem Wisch noch verschluckt wird. */
  swiped(): boolean;
  destroy(): void;
}

export function createSwipeRail(opts: SwipeRailOptions): SwipeRail {
  const { rail, track } = opts;

  let active = 0;
  let offset = 0;
  let swipedRecently = false;

  function count(): number {
    return track.children.length;
  }

  /** Verschiebung, bei der Element `index` an seinem Platz steht. */
  function offsetFor(index: number): number {
    const item = track.children[index] as HTMLElement | undefined;
    if (!item) return 0;
    if (opts.align === 'start') return -item.offsetLeft;
    return rail.clientWidth / 2 - (item.offsetLeft + item.offsetWidth / 2);
  }

  function applyOffset(value: number, animate: boolean, duration?: number): void {
    offset = value;
    track.style.transition = animate ? `transform ${duration ?? 420}ms ${SETTLE}` : 'none';
    track.style.transform = `translate3d(${value}px, 0, 0)`;
  }

  /**
   * Einschwingzeit abhaengig von der Strecke: ein kurzer Ruck springt schnell
   * ein, ein weiter Schwung durch mehrere Elemente darf sich mehr Zeit lassen
   * und dabei langsam auslaufen. So entsteht "weich ausbremsen" statt eines
   * harten Snaps, egal wie viele Elemente uebersprungen werden.
   */
  function settleDuration(distance: number): number {
    return Math.min(SETTLE_MAX, Math.max(SETTLE_MIN, 300 + Math.abs(distance) * 0.42));
  }

  /** Laesst die Spur ueber den Enden nachgeben, statt hart zu stoppen. */
  function rubberBand(value: number): number {
    const max = offsetFor(0);
    const min = offsetFor(count() - 1);
    if (value > max) return max + (value - max) * RUBBER;
    if (value < min) return min + (value - min) * RUBBER;
    return value;
  }

  /** Welches Element stuende bei dieser Verschiebung am naechsten zur Mitte? */
  function nearestIndex(value: number): number {
    let best = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < count(); i += 1) {
      const distance = Math.abs(offsetFor(i) - value);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }
    return best;
  }

  function goTo(index: number, animate = true, duration?: number): void {
    active = Math.min(Math.max(index, 0), Math.max(0, count() - 1));
    const target = offsetFor(active);
    applyOffset(target, animate, duration ?? settleDuration(target - offset));
    opts.onIndexChange?.(active);
  }

  let drag: {
    id: number;
    startX: number;
    startY: number;
    base: number;
    /** Element, das beim Aufsetzen in der Mitte stand. */
    from: number;
    lastX: number;
    lastT: number;
    velocity: number;
    moved: boolean;
    /** Sobald klar ist, dass senkrecht gewischt wird, halten wir uns raus. */
    vertical: boolean;
  } | null = null;

  function onDown(ev: PointerEvent): void {
    // Der Stift zeichnet, sobald ein Werkzeug an ist – dann keine Geste.
    if (drawsWith(ev)) return;
    if (count() < 2) return;
    if (opts.ignore?.(ev.target as HTMLElement)) return;

    drag = {
      id: ev.pointerId,
      startX: ev.clientX,
      startY: ev.clientY,
      base: offset,
      from: active,
      lastX: ev.clientX,
      lastT: performance.now(),
      velocity: 0,
      moved: false,
      vertical: false,
    };
    // Waehrend der Finger zieht, folgt die Spur ohne Uebergang.
    track.style.transition = 'none';
  }

  function onMove(ev: PointerEvent): void {
    if (!drag || ev.pointerId !== drag.id) return;
    const dx = ev.clientX - drag.startX;
    const dy = ev.clientY - drag.startY;

    if (!drag.moved && !drag.vertical) {
      // Erst ab ein paar Pixeln entscheiden, wohin die Geste gehoert:
      // senkrecht scrollt die Seite, waagerecht blaettert.
      if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) {
        drag.vertical = true;
        return;
      }
      if (Math.abs(dx) < 6) return;
      drag.moved = true;
      rail.classList.add('swipe-rail--dragging');

      /*
       * Erst JETZT den Zeiger festhalten, nicht schon beim Aufsetzen.
       *
       * Ohne Festhalten bekommt die Schiene kein pointerup, wenn der Finger
       * ausserhalb losgelassen wird – und genau das passiert am Ende eines
       * weiten Wisches staendig; die Spur bliebe im Gummiband haengen.
       * Haelt man dagegen schon beim Aufsetzen fest, wandert auch das
       * anschliessende click-Ereignis auf die Schiene, und ein einfaches
       * Tippen auf ein Element kaeme nie an. Also: festhalten, sobald
       * feststeht, dass gewischt und nicht getippt wird.
       */
      try {
        rail.setPointerCapture(ev.pointerId);
      } catch {
        /* ohne Festhalten weiter, der Waechter auf window faengt es ab */
      }
    }
    if (drag.vertical) return;

    // Geschwindigkeit aus dem letzten Stueck: sie entscheidet gleich mit.
    const now = performance.now();
    const dt = now - drag.lastT;
    if (dt > 0) drag.velocity = (ev.clientX - drag.lastX) / dt;
    drag.lastX = ev.clientX;
    drag.lastT = now;

    applyOffset(rubberBand(drag.base + dx), false);

    // Waehrend des Ziehens schon zeigen, was in der Mitte steht.
    const near = nearestIndex(offset);
    if (near !== active) {
      active = near;
      opts.onIndexChange?.(active);
    }
  }

  function onUp(ev: PointerEvent): void {
    if (!drag || ev.pointerId !== drag.id) return;
    try {
      if (rail.hasPointerCapture(ev.pointerId)) rail.releasePointerCapture(ev.pointerId);
    } catch {
      /* war nicht festgehalten */
    }
    const { moved, velocity } = drag;
    drag = null;
    rail.classList.remove('swipe-rail--dragging');
    if (!moved) return;

    // Der Browser schickt nach dem Loslassen noch ein click-Ereignis – ohne
    // diese Sperre wuerde am Ende jedes Wisches etwas geoeffnet.
    swipedRecently = true;
    setTimeout(() => {
      swipedRecently = false;
    }, CLICK_GUARD);

    /*
     * Momentum statt "ein Wisch = ein Element": die Position beim Loslassen
     * wird um den Schwung weiterprojiziert (Geschwindigkeit * MOMENTUM_MS),
     * und das naechstgelegene Element zu DIESER Stelle gewinnt. Ein
     * langsamer Zug landet damit praezise dort, wo der Finger war; ein
     * schneller, kurzer Wisch traegt weiter und kann mehrere Elemente
     * ueberspringen – abhaengig von Tempo UND Richtung, wie im Auftrag.
     */
    const projected = offset + velocity * MOMENTUM_MS;
    goTo(nearestIndex(projected));
  }

  function onKey(ev: KeyboardEvent): void {
    if (ev.key === 'ArrowRight') goTo(active + 1);
    else if (ev.key === 'ArrowLeft') goTo(active - 1);
    else return;
    ev.preventDefault();
  }

  function recenter(): void {
    applyOffset(offsetFor(active), false);
  }

  rail.addEventListener('pointerdown', onDown);
  rail.addEventListener('pointermove', onMove);
  rail.addEventListener('pointerup', onUp);
  rail.addEventListener('pointercancel', onUp);
  rail.addEventListener('keydown', onKey);
  // Sicherheitsnetz, falls das Festhalten nicht greift: sonst bliebe die
  // Spur nach einem Wisch, der ausserhalb endet, im Gummiband stehen.
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
  window.addEventListener('resize', recenter);

  return {
    index: () => active,
    goTo,
    recenter,
    swiped: () => swipedRecently,
    destroy() {
      rail.removeEventListener('pointerdown', onDown);
      rail.removeEventListener('pointermove', onMove);
      rail.removeEventListener('pointerup', onUp);
      rail.removeEventListener('pointercancel', onUp);
      rail.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('resize', recenter);
    },
  };
}
